import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Image as ImageIcon, PenTool, Film, Video, Download, Loader2, Eye, Pencil, Play, AlertTriangle, Star, QrCode, Infinity as InfinityIcon } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ImageEditor from "@/components/ImageEditor";
import { RecomposeDialog } from "@/components/RecomposeDialog";
import NoProject from "@/components/NoProject";
import { toast } from "sonner";
import { DeleteX } from "@/components/DeleteX";
import { OverlayEditor } from "@/components/OverlayEditor";
import { fiveTags } from "@/lib/hashtags";
import { downloadFile, saveBlob, mediaUrl } from "@/lib/download";
import { LibrarySourcePicker } from "@/components/LibrarySourcePicker";

const typeMeta = {
  image: { icon: ImageIcon, label: "صورة", color: "text-accent" },
  video: { icon: Video, label: "فيديو", color: "text-sky-400" },
  caption: { icon: PenTool, label: "منشور", color: "text-primary" },
  script: { icon: Film, label: "سكربت", color: "text-fuchsia-400" },
};

// Media is listed light (no base64) and fetched per card when it scrolls into view.
function LazyThumb({ item, onLoaded }) {
  const ref = useRef(null);
  const [src, setSrc] = useState(item.data ? `data:image/png;base64,${item.data}` : null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (src || !item.has_data || !ref.current) return;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting || busy) return;
      setBusy(true);
      api.get(`/content/${item.id}`).then((r) => { setSrc(`data:image/png;base64,${r.data.data}`); onLoaded(r.data); }).catch(() => {}).finally(() => setBusy(false));
      io.disconnect();
    }, { rootMargin: "200px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [item.id, item.has_data, src, busy, onLoaded]);
  return (
    <div ref={ref} className="w-full h-full flex items-center justify-center">
      {src ? <img src={src} alt={item.title} className="w-full h-full object-cover" data-testid={`thumb-${item.id}`} />
        : <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />}
    </div>
  );
}

export default function ContentLibrary() {
  const { activeProject, projects = [], selectProject } = useProjects();
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [items, setItems] = useState([]);
  const toggleFeature = async (it) => {
    try {
      const { data } = await api.put(`/admin/content/${it.id}/feature`, { featured: !it.featured });
      setItems((xs) => xs.map((x) => (x.id === it.id ? { ...x, featured: data.featured } : x)));
      toast.success(data.featured ? "أُضيف إلى معرض الصفحة الرئيسية" : "أُزيل من المعرض");
    } catch (e) { toast.error(formatError(e?.response?.data?.detail) || "تعذّر التحديث"); }
  };
  const [filter, setFilter] = useState("all");
  const [coll, setColl] = useState("all");  // تصفية مكتبة التوليد الفردي بالاسم المستقل
  const [recompose, setRecompose] = useState(null);  // تعديل بيانات قطعة تصميم وإعادة تركيبها بلا رصيد
  const [params, setParams] = useSearchParams();  // ?individual=1&sp=<مشروع توليد فردي> — قائمة الهيدر تتحكم بالاختيار والتنقل
  const scope = params.get("individual") === "1" || (!activeProject && params.get("individual") !== "0") ? "individual" : "project";
  const sp = params.get("sp") || "";
  const setScope = (sc) => { const q = new URLSearchParams(params); if (sc === "individual") q.set("individual", "1"); else { q.set("individual", "0"); q.delete("sp"); } setParams(q); };
  const setSp = (id) => { const q = new URLSearchParams(params); q.set("individual", "1"); if (id) q.set("sp", id); else q.delete("sp"); setParams(q); };
  const proj = scope === "project" ? activeProject : null;
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const [editing, setEditing] = useState(null);
  const [player, setPlayer] = useState(null); // {item, url, loading}

  const load = () => {
    // بلا مشروع نشط: مكتبة التوليد الفردي (كل ما ولّده العميل خارج المشاريع)
    setLoading(true);
    if (scope === "project" && !proj) { setItems([]); setLoading(false); return; }
    api.get(proj ? `/content?project_id=${proj.id}` : "/content").then((r) => setItems(r.data)).catch(() => toast.error("تعذّر تحميل المكتبة")).finally(() => setLoading(false));
  };
  useEffect(load, [activeProject, scope]); // eslint-disable-line react-hooks/exhaustive-deps
  const [studioNames, setStudioNames] = useState({});
  useEffect(() => { if (scope === "individual") api.get("/me/studio-projects").then((r) => setStudioNames(Object.fromEntries(r.data.map((p) => [p.id, p.name])))).catch(() => {}); }, [scope]);

  const patch = (full) => setItems((xs) => xs.map((x) => (x.id === full.id ? { ...x, ...full } : x)));

  const remove = async (id) => {
    try { await api.delete(`/content/${id}`); setItems(items.filter((i) => i.id !== id)); toast.success("تم الحذف"); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const fetchFull = async (it) => {
    if (it.data || it.url) return it;
    const { data } = await api.get(`/content/${it.id}`);
    patch(data);
    return data;
  };

  const openVideo = async (it) => {
    setPlayer({ item: it, url: it.url || null, loading: !it.url });
    try { const full = await fetchFull(it); setPlayer({ item: full, url: full.url, loading: false }); }
    catch { setPlayer(null); toast.error("تعذّر تحميل الفيديو"); }
  };

  const openEditor = async (it) => {
    try { setEditing(await fetchFull(it)); } catch { toast.error("تعذّر تحميل الصورة"); }
  };


  const collections = [...new Set(items.map((i) => (i.collection || "").trim()).filter(Boolean))];
  const bySp = sp ? items.filter((i) => i.project_id === sp) : items;
  const scoped = coll === "all" ? bySp : bySp.filter((i) => (i.collection || "").trim() === coll);
  const filtered = filter === "all" ? scoped : scoped.filter((i) => i.type === filter);
  const count = (t) => scoped.filter((i) => i.type === t).length;
  const exportAll = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/content/export?project_id=${proj?.id || ""}`, { responseType: "blob", timeout: 600000 });
      await saveBlob(r.data, `${proj?.name || "individual"}.zip`);
      toast.success("تم تجهيز ملف ZIP بكل المحتوى");
    } catch (e) { toast.error("تعذّر تنزيل المكتبة"); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="library-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-heading">مكتبة المحتوى</h1>
          <p className="text-muted-foreground">{proj ? `جميع المحتوى المولّد للمشروع الاستراتيجي «${proj.name}»` : sp && studioNames[sp] ? `محتوى مشروع التوليد الفردي «${studioNames[sp]}»` : "كل ما ولّدته في مشاريع التوليد الفردي — مستقل عن المشاريع الاستراتيجية"}</p>
          <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1" data-testid="library-unlimited-note"><InfinityIcon className="w-3.5 h-3.5" /> كل ما ولّدته يبقى هنا: عدّل البيانات والصورة الشخصية والنصوص ونزّل PNG/PDF في أي وقت وبلا حدود — بلا رصيد.</p>
        </div>
        {items.length > 0 && (
          <Button variant="outline" className="gap-2 border-border" onClick={exportAll} disabled={exporting} data-testid="library-export-all-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            تنزيل الكل (ZIP)
          </Button>
        )}
      </div>
      <LibrarySourcePicker scope={scope} setScope={setScope} sp={sp} setSp={setSp} projects={projects} activeProject={activeProject} selectProject={selectProject} />
      {!proj && collections.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="collection-filter">
          <span className="text-xs text-muted-foreground">الاسم المستقل:</span>
          {["all", ...collections].map((c) => (
            <button key={c} type="button" onClick={() => setColl(c)} data-testid={`collection-chip-${c === "all" ? "all" : "x"}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${coll === c ? "bg-primary text-white border-primary" : "bg-secondary/60 border-border hover:border-primary/40"}`}>{c === "all" ? "الكل" : c}</button>
          ))}
        </div>
      )}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-secondary flex-wrap h-auto">
          <TabsTrigger value="all" data-testid="filter-all">الكل ({scoped.length})</TabsTrigger>
          <TabsTrigger value="image" data-testid="filter-image">الصور ({count("image")})</TabsTrigger>
          <TabsTrigger value="video" data-testid="filter-video">الفيديو ({count("video")})</TabsTrigger>
          <TabsTrigger value="caption" data-testid="filter-caption">المنشورات ({count("caption")})</TabsTrigger>
          <TabsTrigger value="script" data-testid="filter-script">السكربتات ({count("script")})</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      : filtered.length === 0 ? (
        <div className="glass rounded-2xl p-16 text-center border border-border"><p className="text-muted-foreground">لا يوجد محتوى في هذا التصنيف بعد.</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((it) => {
            const m = typeMeta[it.type] || typeMeta.caption;
            const failed = it.status === "failed";
            const processing = it.status === "processing";
            const hasImg = it.type === "image" && (it.has_data || it.data);
            const hasVid = it.type === "video" && (it.has_url || it.url);
            const isDesign = ["business_card", "menu", "brochure", "cover", "infographic", "logo"].includes(it.item_kind);
            const printable = ["business_card", "menu", "brochure", "cover", "infographic", "logo", "post_image", "image"].includes(it.item_kind || "image");
            return (
              <div key={it.id} className="glass rounded-2xl border border-border overflow-hidden card-hover" data-testid={`library-item-${it.id}`}>
                <div className="relative aspect-square bg-secondary flex items-center justify-center overflow-hidden">
                  <DeleteX testId={`delete-item-${it.id}`} className="absolute top-2 end-2 z-10" onConfirm={() => remove(it.id)} />
                  {hasImg ? <LazyThumb item={it} onLoaded={patch} />
                  : hasVid ? (
                    <button type="button" onClick={() => openVideo(it)} className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gradient-to-b from-sky-500/10 to-primary/20 hover:from-sky-500/20 transition-colors" data-testid={`play-item-${it.id}`}>
                      <span className="w-14 h-14 rounded-full bg-white/90 text-black flex items-center justify-center shadow-lg"><Play className="w-6 h-6 mr-[-2px]" /></span>
                      <span className="text-xs text-muted-foreground">اضغط للتشغيل</span>
                    </button>
                  ) : failed ? (
                    <div className="text-center px-4"><AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-1" /><p className="text-[11px] text-red-300 leading-relaxed line-clamp-3">{it.error || "فشل التوليد"}</p></div>
                  ) : processing ? (
                    <div className="text-center"><Loader2 className="w-7 h-7 animate-spin text-primary mx-auto mb-1" /><p className="text-xs text-muted-foreground">قيد المعالجة {it.progress ? `${it.progress}%` : ""}</p></div>
                  ) : (
                    <m.icon className={`w-12 h-12 ${m.color} opacity-50`} />
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <m.icon className={`w-4 h-4 ${m.color}`} />
                    <span className="text-xs text-muted-foreground">{m.label} · {it.platform}</span>
                    {failed && <span className="text-[10px] bg-red-500/15 text-red-300 px-1.5 py-0.5 rounded-full ms-auto">فشل</span>}
                  </div>
                  <h3 className="font-semibold text-sm truncate mb-1">{it.title}</h3>
                  {it.collection ? <span className="inline-block text-[10px] rounded-full bg-primary/10 text-primary px-2 py-0.5 mb-2 max-w-full truncate" data-testid={`item-collection-${it.id}`}>{it.collection}</span> : <span className="block mb-2" />}
                  <div className="grid grid-cols-2 gap-2" data-testid={`item-actions-${it.id}`}>
                    {(it.type === "caption" || it.type === "script") && (
                      <Button size="sm" variant="outline" className="flex-1 gap-1 border-border h-8 text-xs" onClick={() => setView(it)} data-testid={`view-item-${it.id}`}><Eye className="w-3 h-3" /> عرض</Button>
                    )}
                    {hasImg && (
                      <Button size="sm" className="flex-1 gap-1 h-8 text-xs" onClick={() => (isDesign && !failed ? setRecompose(it) : openEditor(it))} title={isDesign ? "عدّل البيانات (الأسماء، الأرقام، الأصناف، QR) أو حرّر النصوص والشعار يدويًا — بلا رصيد" : "حرّر النصوص والشعار وحمّل الصورة"} data-testid={`edit-item-${it.id}`}><Pencil className="w-3 h-3" /> تعديل</Button>
                    )}
                    {hasImg && !failed && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" className="flex-1 gap-1 border-primary/40 text-primary h-8 text-xs" data-testid={`download-menu-${it.id}`}><Download className="w-3 h-3" /> تنزيل</Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" dir="rtl" className="min-w-[200px]">
                          <DropdownMenuItem onClick={() => openEditor(it)} data-testid={`download-png-${it.id}`}>صورة PNG عالية الدقة</DropdownMenuItem>
                          {printable && (
                            <DropdownMenuItem data-testid={`print-pdf-${it.id}`} onClick={async () => { try { const r = await api.get(`/content/${it.id}/pdf`, { responseType: "blob" }); await saveBlob(r.data, `${it.item_kind || "image"}-A4.pdf`); } catch { toast.error("تعذّر إنشاء ملف الطباعة"); } }}>
                              {it.item_kind === "logo" ? "PDF + ملف الهوية" : it.item_kind === "business_card" ? "PDF للطباعة — A4 فيه 8 بطاقات" : "PDF للطباعة (A4)"}
                            </DropdownMenuItem>
                          )}
                          {it.item_kind === "logo" && (
                            <DropdownMenuItem data-testid={`logo-pack-${it.id}`} onClick={async () => { try { const r = await api.get(`/content/${it.id}/logo-pack`, { responseType: "blob" }); await saveBlob(r.data, "logo-pack.zip"); } catch { toast.error("تعذّر تجهيز حزمة اللوقو"); } }}>حزمة اللوقو ZIP (شفاف + أبيض + أسود + الهوية)</DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {hasImg && !failed && it.item_kind === "business_card" && (
                      <a href={`/card/${it.id}`} target="_blank" rel="noreferrer" className="col-span-2 inline-flex items-center justify-center gap-1 rounded-md border border-emerald-500/40 text-emerald-700 dark:text-emerald-400 h-8 text-xs px-2" title="البطاقة الرقمية التي يفتحها رمز QR — حفظ جهة الاتصال والقنوات والمشاركة" data-testid={`digital-card-${it.id}`}><QrCode className="w-3 h-3" /> البطاقة الرقمية — شارك واحفظ جهة الاتصال</a>
                    )}
                    {hasVid && (
                      <Button size="sm" variant="outline" className="flex-1 gap-1 border-border h-8 text-xs" onClick={() => openVideo(it)} data-testid={`open-item-${it.id}`}><Play className="w-3 h-3" /> تشغيل وتنزيل</Button>
                    )}
                    {failed && <Button size="sm" variant="outline" className="flex-1 gap-1 border-border h-8 text-xs text-red-300" onClick={() => remove(it.id)} data-testid={`remove-failed-${it.id}`}>إزالة</Button>}
                  </div>
                  <div className="flex gap-2 mt-2">
                    {(hasImg || hasVid) && !failed && !isDesign && (
                      <OverlayEditor contentId={it.id} testId={`overlay-item-${it.id}`} className="flex-1 h-8 text-xs"
                        onUpdated={(d) => setItems((xs) => xs.map((x) => x.id !== it.id ? x : { ...x, ...(d.data ? { data: d.data } : {}), ...(d.url ? { url: d.url } : {}) }))} />
                    )}
                    {user?.role === "admin" && (hasImg || hasVid) && !failed && (
                      <Button size="sm" variant="outline" onClick={() => toggleFeature(it)} title={it.featured ? "معروض في معرض الرئيسية — اضغط للإخفاء" : "اعرض في معرض الرئيسية"} className={`h-8 text-xs gap-1 ${it.featured ? "border-amber-500 text-amber-500" : "border-border"} ${isDesign ? "flex-1" : "px-2"}`} data-testid={`feature-item-${it.id}`}>
                        <Star className={`w-3 h-3 ${it.featured ? "fill-amber-500" : ""}`} /> {isDesign ? (it.featured ? "معروض في الرئيسية" : "اعرض في معرض الرئيسية") : ""}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!player} onOpenChange={() => setPlayer(null)}>
        <DialogContent className="bg-popover border-border max-w-md p-4" dir="rtl" data-testid="video-player-dialog">
          <DialogHeader><DialogTitle className="text-base truncate">{player?.item?.title}</DialogTitle></DialogHeader>
          {player?.loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3"><Loader2 className="w-8 h-8 animate-spin text-primary" /><p className="text-sm text-muted-foreground">جارٍ تحميل الفيديو…</p></div>
          ) : player?.url ? (
            <div className="space-y-3">
              <video src={mediaUrl(player.url)} controls autoPlay playsInline className="w-full rounded-xl bg-black max-h-[65vh]" data-testid="video-player" />
              <Button variant="outline" className="w-full gap-2 border-border" onClick={() => downloadFile(player.url, "video.mp4")} data-testid="video-download-btn"><Download className="w-4 h-4" /> تنزيل الفيديو</Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="bg-popover border-border max-w-lg max-h-[85vh] overflow-y-auto" dir="rtl">
          <DialogHeader><DialogTitle>{view?.title}</DialogTitle></DialogHeader>
          {view?.type === "caption" && view.text && (
            <div className="space-y-3">
              <p className="text-sm whitespace-pre-line leading-relaxed">{view.text.caption}</p>
              {view.text.cta && <p className="text-sm text-accent font-semibold">{view.text.cta}</p>}
              {view.text.hashtags && <div className="flex flex-wrap gap-2">{fiveTags(view.text.hashtags).map((h, i) => <span key={i} className="text-xs bg-primary/15 text-primary px-2 py-1 rounded-full">{h}</span>)}</div>}
            </div>
          )}
          {view?.type === "script" && view.text && (
            <div className="space-y-3">
              {view.text.hook && <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 text-sm">{view.text.hook}</div>}
              {(view.text.scenes || []).map((sc, i) => (
                <div key={i} className="bg-secondary rounded-lg p-3">
                  <div className="text-xs text-primary font-mono mb-1">{sc.timestamp}</div>
                  <div className="text-sm">{sc.visual}</div>
                  <div className="text-sm text-muted-foreground">{sc.voiceover}</div>
                </div>
              ))}
              {view.text.cta && <p className="text-sm text-accent font-semibold">{view.text.cta}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {recompose && <RecomposeDialog item={recompose} onClose={() => setRecompose(null)} onSaved={load} onAdvanced={(full) => { setRecompose(null); setEditing(full); }} />}
      <ImageEditor open={!!editing} item={editing} projectLogo={editing?.logo || (editing?.project_id && editing.project_id === activeProject?.id ? activeProject?.logo : null)} brandName={editing?.ov?.brand_name || editing?.brand_name || ""} onClose={() => setEditing(null)} allowLogo={editing?.item_kind !== "logo"} fileName={editing?.item_kind || "image"} />
    </div>
  );
}
